import { useMemo } from 'react';

import { Metrics } from 'components/insights/constants';
import { formatPercentage, formatResponseTime } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';

import MetricDetailLayout from '../../shared/slideout/MetricDetailLayout';
import { StatTile } from '../../shared/slideout/statTiles';
import { DENIAL_CATEGORY_ORDER, denialReasonLabel, isRealDimensionValue } from '../constants';

import InboundDenialBreakdownChart, {
  DENIAL_BREAKDOWN_CHART_HEIGHT,
  DenialCategoryRow,
} from './InboundDenialBreakdownChart';

type Props = {
  loading: boolean;
  initialLoading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  data: AnalyticsResponseData;
};

// Metric drill for the Denied rate tile: denial rate + avg decision time + top denial reason, over a
// donut of denials by category. Everything here is dimension-independent (the tiles read the
// grand-total rollup; the donut/top-reason are the same denials-by-category regardless of the table's
// View By), so it all gates on `initialLoading` — under keepPreviousData the values stay put on a
// switch. The donut + top-reason are derived from the main query's grouping-0 leaves (the same leaves
// the breakdown table's sub-rows parse), summed over the entity dim — no separate query.
export default function DeniedDetailHeader({ initialLoading, rollups, data }: Props) {
  const overall = rollups.overall.metrics;
  const denialRate = overall.getCurrentDataPoint(Metrics.DENIAL_RATE, 0);
  const deniedCount = overall.getCurrentDataPoint(Metrics.DENIED_COUNT, 0) ?? 0;
  const totalReceived = overall.getCurrentDataPoint(Metrics.REFERRAL_COUNT, 0) ?? 0;
  const avgTimeToDenialSeconds = overall.getCurrentDataPoint(Metrics.TIME_TO_DENIAL_DECISION_SECONDS_AVERAGE, 0);

  const { categoryRows, topReason, totalDenied } = useMemo(() => {
    const categories = data.dimensions.getCurrentDimensionValuesByIndex(1);
    const descriptions = data.dimensions.getCurrentDimensionValuesByIndex(2);
    const denied = data.metrics.getCurrentSeries(Metrics.DENIED_COUNT);

    const byCategory = new Map<string, number>();
    // A (category, description) reason appears once per entity leaf; aggregate across entities before
    // ranking the top reason.
    const byReason = new Map<string, { category: string; description: string; count: number }>();
    categories.forEach((category, i) => {
      const description = descriptions[i];
      const count = denied[i] ?? 0;
      if (!isRealDimensionValue(category) || count <= 0) return;
      byCategory.set(category, (byCategory.get(category) ?? 0) + count);
      if (isRealDimensionValue(description)) {
        const key = `${category}\x00${description}`;
        const existing = byReason.get(key);
        if (existing) existing.count += count;
        else byReason.set(key, { category, description, count });
      }
    });

    const rows: DenialCategoryRow[] = DENIAL_CATEGORY_ORDER.map((category) => ({
      category,
      count: byCategory.get(category) ?? 0,
    })).filter((row) => row.count > 0);

    const top = [...byReason.values()].sort((a, b) => b.count - a.count)[0];
    const topReasonValue = top ? { label: denialReasonLabel(top.category, top.description), count: top.count } : null;

    return { categoryRows: rows, topReason: topReasonValue, totalDenied: rows.reduce((sum, r) => sum + r.count, 0) };
  }, [data]);

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label='Avg. Denial Rate'
            tone='red'
            loading={initialLoading}
            inlineAddendum
            primary={formatPercentage(denialRate)}
            addendum={denialRate != null ? `${deniedCount} of ${totalReceived} referrals received` : undefined}
          />
          <StatTile
            label='Avg. Time to Denial'
            loading={initialLoading}
            primary={formatResponseTime(avgTimeToDenialSeconds)}
          />
          <StatTile label='Top Denial Reason' loading={initialLoading} primary={topReason?.label ?? '—'} />
        </>
      }
      chartTitle='Denial Breakdown'
      chartLoading={initialLoading}
      chartHeight={DENIAL_BREAKDOWN_CHART_HEIGHT}
      isEmpty={categoryRows.length === 0}
      emptyMessage='No denial reasons to show for this time period.'>
      <InboundDenialBreakdownChart categoryRows={categoryRows} totalDenied={totalDenied} />
    </MetricDetailLayout>
  );
}
